import { makeAutoObservable, runInAction } from 'mobx';
import { hitApi } from '../database/services/ApiHelper';
import { API } from '../database/apis';
import { User, Book, Category, LoginBody, SignupBody } from '../types';
import localforage from 'localforage';

// D2P returns categories as a static list shared with the website.
// Kept inline so the standalone app doesn't need a network call for them.
const D2P_CATEGORIES: string[] = [
  'Action & Adventure',
  'Adventure',
  'Anthologies',
  'Art & Photography',
  'Biography & Memoir',
  'Business & Economics',
  "Children's Picture Books",
  'Computers & Internet',
  'Contemporary Fiction',
  'Cooking, Food & Wine',
  'Crafts, Hobbies & Home',
  'Crime',
  'Cultural Studies',
  'Current Events',
  'Cyberpunk',
  'Detective',
  'Drama',
  'Dystopian',
  'Education',
  'Epic Fantasy',
  'Fairy Tales & Folklore',
  'Fantasy',
  'Fiction (General)',
  'Gardening',
  'Graphic Novels & Comics',
  'Health & Wellness',
  'Historical Fiction',
  'History',
  'Horror',
  'Humor',
  'Inspirational',
  'Literary Fiction',
  'Mystery',
  'Mythology',
  'Nature',
  'Non-Fiction (General)',
  'Paranormal',
  'Parenting & Family',
  'Personal Development',
  'Philosophy',
  'Photography',
  'Poetry',
  'Politics',
  'Religion & Spirituality',
  'Romance',
  'Science Fiction',
  'Self-Help',
  'Short Stories',
  'Sports & Outdoors',
  'Steampunk',
  'Suspense',
  'Technology',
  'Thriller',
  'Travel',
  'True Crime',
  'Urban Fantasy',
  'Western',
  'Young Adult',
];

// Maps a D2P /auth/login or /auth/status `user` payload onto the existing
// kleverbook User shape (which uses _id everywhere). Fields D2P doesn't expose
// (phone, address, etc.) stay empty — the legacy profile-edit screen shows
// blank fields rather than misleading data.
function mapD2pUserToKleverUser(d2pUser: any): User {
  return {
    _id: d2pUser?.id ?? '',
    name: d2pUser?.name ?? '',
    email: d2pUser?.email ?? '',
    phone: '',
    photo: d2pUser?.avatarUrl ?? undefined,
  };
}

class UserStore {
  user: User | null = null;
  authToken: string = '';
  // Anonymous device token issued by /api/d2p/kleverbook/anon-session.
  // Persistent (server never expires it). Used as the Bearer token when
  // the user is not logged in, so the catalog/reader/ad pipeline works
  // with zero signup friction. A real user login overrides it.
  deviceToken: string = '';
  deviceId: string = '';
  notificationToken: string = '';
  adverBooks: Book[] = [];
  bookCat: Category[] = [];
  loginLoader = false;
  regLoader = false;
  adverBookLoader = false;
  catLoader = false;
  photoUploading = false;

  // Role flags from D2P. KleverBook is a reader app — we don't switch UI based
  // on role, but authors get an "Author Tools" entry point in Profile that
  // deep-links into the d2p website's author dashboard.
  isAuthor: boolean = false;
  isApprovedAuthor: boolean = false;
  authorId: string | null = null;
  requiresOnboarding: boolean = false;

  constructor() {
    makeAutoObservable(this);
    this.hydrate();
    // Categories are static for D2P — seed once
    this.bookCat = D2P_CATEGORIES.map((name, i) => ({
      _id: `cat-${i}`,
      name,
      selected: false,
    }));
  }

  setNotificationToken(token: string) {
    this.notificationToken = token;
  }

  async hydrate() {
    try {
      const savedUser = await localforage.getItem<string>('user');
      const savedToken = await localforage.getItem<string>('authToken');
      const savedRoles = await localforage.getItem<string>('roleFlags');
      const savedDeviceId = await localforage.getItem<string>('deviceId');
      const savedDeviceToken = await localforage.getItem<string>('deviceToken');
      if (savedUser) {
        runInAction(() => {
          this.user = JSON.parse(savedUser);
        });
      }
      if (savedToken) {
        runInAction(() => {
          this.authToken = savedToken;
        });
      }
      if (savedDeviceId) {
        runInAction(() => {
          this.deviceId = savedDeviceId;
        });
      }
      if (savedDeviceToken) {
        runInAction(() => {
          this.deviceToken = savedDeviceToken;
        });
      }
      if (savedRoles) {
        try {
          const r = JSON.parse(savedRoles);
          runInAction(() => {
            this.isAuthor = !!r.isAuthor;
            this.isApprovedAuthor = !!r.isApprovedAuthor;
            this.requiresOnboarding = !!r.requiresOnboarding;
            this.authorId = r.authorId ?? null;
          });
        } catch {
          // ignore corrupt cache; /auth/status will refill on next call
        }
      }
    } catch (e) {
      console.log('Hydration error:', e);
    }
  }

  async persist() {
    try {
      await localforage.setItem('user', JSON.stringify(this.user));
      await localforage.setItem('authToken', this.authToken);
      await localforage.setItem('deviceId', this.deviceId);
      await localforage.setItem('deviceToken', this.deviceToken);
      await localforage.setItem(
        'roleFlags',
        JSON.stringify({
          isAuthor: this.isAuthor,
          isApprovedAuthor: this.isApprovedAuthor,
          requiresOnboarding: this.requiresOnboarding,
          authorId: this.authorId,
        })
      );
    } catch (e) {
      console.log('Persist error:', e);
    }
  }

  get isLoggedIn() {
    return !!this.user && !!this.authToken;
  }

  // The token used as Bearer for KleverBook API calls. Real user JWT wins;
  // otherwise the persistent anon device token from /anon-session. Empty
  // string only on a brand-new install before the bootstrap call returns.
  get effectiveToken(): string {
    return this.authToken || this.deviceToken || '';
  }

  // Bootstrap an anonymous reader session on app launch. Idempotent —
  // calling repeatedly only refreshes the lastSeenAt server-side. Safe to
  // call even when the user is logged in (we still keep a device token in
  // case they log out later).
  async bootstrapDeviceSession() {
    try {
      let id = this.deviceId;
      if (!id) {
        id = (typeof crypto !== 'undefined' && (crypto as any).randomUUID
          ? (crypto as any).randomUUID()
          : `dev_${Date.now()}_${Math.random().toString(36).slice(2, 14)}`);
        runInAction(() => {
          this.deviceId = id;
        });
      }
      const res: any = await hitApi({
        endpoint: 'api/d2p/kleverbook/anon-session',
        method: 'POST',
        body: { deviceId: id, platform: 'web' },
      });
      if (res?.token) {
        runInAction(() => {
          this.deviceToken = res.token;
        });
        await this.persist();
      }
    } catch (e) {
      console.log('Anon session bootstrap error:', e);
    }
  }

  async attemptToLogin(body: LoginBody, callback: (success: boolean, message?: string) => void) {
    this.loginLoader = true;
    try {
      const res = await hitApi({
        endpoint: API.LOGIN,
        method: 'POST',
        body: { email: body.email, password: body.password, rememberMe: true },
      });

      // D2P may respond 200 with { ok: false, isC5mMemberWithoutD2p: true, ... }
      // for valid C5M users without d2p access. Surface a clear message.
      if (!res?.ok || !res?.token) {
        runInAction(() => { this.loginLoader = false; });
        callback(false, res?.error || 'Login failed');
        return;
      }

      runInAction(() => {
        this.user = mapD2pUserToKleverUser(res.user);
        this.authToken = res.token;
        // Role flags — D2P returns `author: bool`, `isApprovedAuthor: bool`,
        // and `authorData: { id, ... } | null`.
        this.isAuthor = !!res.author;
        this.isApprovedAuthor = !!res.isApprovedAuthor;
        this.requiresOnboarding = !!res.requiresOnboarding;
        this.authorId = res.authorData?.id ?? null;
        this.loginLoader = false;
      });
      await this.persist();
      callback(true);
    } catch (error: any) {
      runInAction(() => { this.loginLoader = false; });
      callback(false, error?.data?.error || error?.message || 'Login failed');
    }
  }

  async registerUser(body: SignupBody, callback: (success: boolean, message?: string) => void) {
    this.regLoader = true;
    try {
      // D2P signup payload differs from the legacy EC2 shape — adapt here so
      // the existing Signup screen doesn't need to change.
      await hitApi({
        endpoint: API.REGISTER,
        method: 'POST',
        body: {
          fullName: body.name,
          email: body.email,
          country: body.country || body.countryCode || 'US',
          password1: body.password,
          password2: body.password,
        },
      });

      // D2P's create-user endpoint doesn't issue a JWT — chain a login call so
      // the user lands logged in, mirroring the old behavior.
      await this.attemptToLogin(
        { email: body.email, password: body.password },
        (success, message) => {
          runInAction(() => { this.regLoader = false; });
          callback(success, message);
        }
      );
    } catch (error: any) {
      runInAction(() => { this.regLoader = false; });
      callback(false, error?.data?.error || error?.message || 'Registration failed');
    }
  }

  async updateUser(body: Partial<User>, callback: (success: boolean, message?: string) => void) {
    if (!this.authToken) {
      callback(false, 'You need to be logged in to edit your profile.');
      return;
    }
    try {
      // Only the fields the D2P endpoint accepts. Address / phone / city
      // etc. on the User model would silently no-op so we drop them rather
      // than mislead the caller into thinking they persisted.
      const payload: Record<string, string | null> = {};
      if (body.name !== undefined) payload.name = body.name as string;
      if ((body as any).countryCode !== undefined) {
        payload.countryCode = (body as any).countryCode;
      }

      const res = await hitApi({
        endpoint: API.USER_PROFILE,
        method: 'PATCH',
        body: payload,
        token: this.authToken,
      });

      runInAction(() => {
        if (this.user) {
          this.user = {
            ...this.user,
            name: res?.name ?? this.user.name,
            photo: res?.avatarUrl ?? this.user.photo,
          };
        }
      });
      await localforage.setItem('user', JSON.stringify(this.user));
      callback(true, 'Profile updated');
    } catch (error: any) {
      callback(
        false,
        error?.data?.error || error?.message || 'Failed to update profile'
      );
    }
  }

  async changePassword(
    _currentPassword: string,
    _newPassword: string,
    callback: (success: boolean, message?: string) => void
  ) {
    callback(
      false,
      'Password changes are handled on the d2p website. Use "Forgot Password" to reset.'
    );
  }

  async forgotPassword(email: string, callback: (success: boolean, message?: string) => void) {
    try {
      await hitApi({
        endpoint: API.FORGOT_PASSWORD,
        method: 'POST',
        body: { email },
      });
      callback(true, 'If that email is registered, a reset link has been sent.');
    } catch (error: any) {
      callback(false, error?.data?.error || error?.message || 'Failed to send reset email');
    }
  }

  async getAdvertisedBooks() {
    this.adverBookLoader = true;
    try {
      const res = await hitApi({
        endpoint: API.ADVERTISED_BOOKS,
        method: 'GET',
        token: this.effectiveToken,
      });
      runInAction(() => {
        // D2P returns { books: [...] } (no res.data wrapper). Map to the
        // existing Book shape the rest of the app already consumes.
        this.adverBooks = (res?.books || []).map((b: any): Book => ({
          _id: b.id,
          title: b.title,
          authorName: b.author?.name ?? 'Anonymous',
          category: b.category,
          categoryName: b.category,
          description: b.description ?? '',
          coverImage: b.coverUrl,
          // Carry stat-strip fields through so Book detail can render
          // them before /books/[id] resolves. The Book detail GET still
          // overrides with fresher values when it lands.
          pageCount: b.pageCount,
          language: b.language,
          ad_slots: Array.isArray(b.adSlots)
            ? b.adSlots.map(String)
            : [],
        }));
        this.adverBookLoader = false;
      });
    } catch (error: any) {
      runInAction(() => { this.adverBookLoader = false; });
    }
  }

  async getCategories() {
    // Categories are static for D2P — already seeded in the constructor.
    // Kept as a no-op to preserve the existing call sites.
  }

  async attemptToGetUser() {
    if (!this.user || !this.authToken) return;
    try {
      const res = await hitApi({
        endpoint: API.AUTH_STATUS,
        method: 'GET',
        token: this.authToken,
      });
      if (res?.isAuthenticated && res?.user) {
        runInAction(() => {
          this.user = mapD2pUserToKleverUser(res.user);
          // /auth/status uses `isAuthor` + `author: { id, ... }` shape
          this.isAuthor = !!res.isAuthor;
          this.isApprovedAuthor = !!res.isApprovedAuthor;
          this.requiresOnboarding = !!res.requiresOnboarding;
          this.authorId = res.author?.id ?? null;
        });
        await this.persist();
      } else {
        // Token invalid/expired on the server — log the user out locally so
        // they're prompted to sign in again.
        await this.logout(() => {});
      }
    } catch (e) {
      // Silent fail — keep existing local state on transient errors
    }
  }

  async getAllData() {
    await Promise.all([
      this.getAdvertisedBooks(),
      this.attemptToGetUser(),
    ]);
  }

  toggleCategorySelection(categoryId: string) {
    const cat = this.bookCat.find((c) => c._id === categoryId);
    if (cat) {
      cat.selected = !cat.selected;
    }
  }

  clearCategorySelections() {
    this.bookCat.forEach((cat) => (cat.selected = false));
  }

  async uploadProfilePhoto(base64Data: string, callback: (success: boolean, message?: string) => void) {
    if (!this.authToken) {
      callback(false, 'You need to be logged in to change your photo.');
      return;
    }
    try {
      runInAction(() => { this.photoUploading = true; });

      // base64Data comes in as `data:image/<fmt>;base64,<...>`.
      // Convert it to a Blob the server can read as a normal file upload.
      const match = base64Data.match(/^data:(image\/[^;]+);base64,(.+)$/);
      if (!match) {
        runInAction(() => { this.photoUploading = false; });
        callback(false, 'Unsupported image format.');
        return;
      }
      const mime = match[1];
      const b64 = match[2];
      const byteString = atob(b64);
      const bytes = new Uint8Array(byteString.length);
      for (let i = 0; i < byteString.length; i++) bytes[i] = byteString.charCodeAt(i);
      const blob = new Blob([bytes], { type: mime });

      // The PATCH endpoint accepts multipart/form-data with an `avatar`
      // field. Server validates type + size (5 MB cap) + uploads to B2 +
      // deletes the old avatar + returns a presigned URL.
      const form = new FormData();
      const ext = mime.split('/')[1] || 'jpg';
      form.append('avatar', blob, `avatar.${ext}`);

      const res = await hitApi({
        endpoint: API.USER_PROFILE,
        method: 'PATCH',
        formData: form,
        token: this.authToken,
      });

      runInAction(() => {
        if (this.user) {
          this.user = {
            ...this.user,
            photo: res?.avatarUrl ?? this.user.photo,
          };
        }
        this.photoUploading = false;
      });
      await localforage.setItem('user', JSON.stringify(this.user));
      callback(true, 'Photo updated');
    } catch (error: any) {
      runInAction(() => { this.photoUploading = false; });
      callback(
        false,
        error?.data?.error || error?.message || 'Failed to upload photo'
      );
    }
  }

  async logout(callback: () => void) {
    this.user = null;
    this.authToken = '';
    this.adverBooks = [];
    this.isAuthor = false;
    this.isApprovedAuthor = false;
    this.requiresOnboarding = false;
    this.authorId = null;
    await localforage.removeItem('user');
    await localforage.removeItem('authToken');
    await localforage.removeItem('roleFlags');
    // Clear owned-library + wishlist cache too, otherwise the next user (or
    // relogged-in same user) would briefly see stale state.
    try {
      const { libraryStore } = await import('./libraryStore');
      await libraryStore.logout();
    } catch {
      // libraryStore not loaded yet — fine on logout-before-login
    }
    try {
      const { wishlistStore } = await import('./wishlistStore');
      await wishlistStore.logout();
    } catch {
      // wishlistStore not loaded yet
    }
    callback();
  }
}

export const userStore = new UserStore();
